/**
 * The semantic status-tone vocabulary (B1/B2, 2026-07-27).
 *
 * Every "how is this doing?" indicator in the product — badges, chips, inline
 * notices, standalone confirmations — maps onto one of five tones, and every
 * tone resolves to theme tokens only. That is the whole point: before this
 * module the same five states were spelled out ~60 times as raw Tailwind
 * palette classes (`bg-emerald-100 text-emerald-800`, …), which are invisible
 * to tenant branding and, in eight places, had no `dark:` counterpart at all.
 *
 * Token contract (see `app/globals.css` and `lib/branding/required-pairs.ts`):
 * - `--success` / `--info` / `--destructive` are legible AS TEXT on
 *   `--background`, so the base token doubles as the text colour over its own
 *   low-alpha wash. The derivation holds them to `TONE` (6:1), not to WCAG's
 *   4.5 — a pair sitting exactly on 4.5 drops to ≈3.5:1 once its own `/15`
 *   wash is laid underneath. B3 fixed `--destructive` here: it used to be
 *   validated the OTHER way round (white on the fill, `destructive/white`) and
 *   the text direction was checked nowhere, so `text-destructive` shipped at
 *   4.46:1 on white and the danger badge at 3.46:1. It is now derived like
 *   success/info, with `--destructive-foreground` carrying the text-on-fill duty.
 * - `--warning` is the remaining exception: it is a bright amber *fill* and its
 *   readable text partner is `--warning-foreground`. Encoded here once so no
 *   call site has to remember the asymmetry — `text-warning` is NOT a legible
 *   text colour on a page surface (2.55:1 in light mode) and must never be used
 *   as one.
 * - `neutral` uses `--muted` / `--muted-foreground`, a pair the branding
 *   derivation already guarantees at 4.5:1.
 *
 * Alpha steps are deliberately uniform (`/15` fill, `/40` hairline) so tones
 * stay comparable next to each other in a table. `tone.test.ts` re-measures
 * every class string this module exports — parsed out of the strings
 * themselves, so a future edit is measured, not trusted — against the derived
 * palette of a full hue sweep, in both modes and on both base surfaces.
 */

/** The five semantic states any status indicator may express. */
export type Tone = "success" | "warning" | "info" | "danger" | "neutral";

/** Ordered for iteration in tests/storybooks — matches escalation severity. */
export const TONES: readonly Tone[] = ["neutral", "success", "info", "warning", "danger"];

/**
 * Badge / chip classes: hairline border, low-alpha wash, readable text.
 * Pair with `<Badge variant="outline">` or any bordered `<span>`.
 */
export const TONE_BADGE_CLASSES: Record<Tone, string> = {
  success: "border-success/40 bg-success/15 text-success",
  warning: "border-warning/40 bg-warning/15 text-warning-foreground",
  info: "border-info/40 bg-info/15 text-info",
  danger: "border-destructive/40 bg-destructive/15 text-destructive",
  neutral: "border-border bg-muted text-muted-foreground",
};

/**
 * Block notice classes (the `role="alert"` / `role="status"` paragraphs used
 * across the admin dialogs). Same palette as the badge, plus the shared box
 * metrics so the ~15 inline copies of `rounded border … px-3 py-2 text-sm`
 * collapse to one constant.
 */
const NOTICE_BOX = "rounded border px-3 py-2 text-sm";
export const TONE_NOTICE_CLASSES: Record<Tone, string> = {
  success: `${NOTICE_BOX} border-success/40 bg-success/10 text-success`,
  warning: `${NOTICE_BOX} border-warning/40 bg-warning/10 text-warning-foreground`,
  info: `${NOTICE_BOX} border-info/40 bg-info/10 text-info`,
  danger: `${NOTICE_BOX} border-destructive/40 bg-destructive/10 text-destructive`,
  neutral: `${NOTICE_BOX} border-border bg-muted text-muted-foreground`,
};

/**
 * Bare text/icon colour for a tone — for check marks, "gespeichert" hints and
 * other indicators that sit directly on a page/card surface with no fill.
 */
export const TONE_TEXT_CLASSES: Record<Tone, string> = {
  success: "text-success",
  warning: "text-warning-foreground",
  info: "text-info",
  danger: "text-destructive",
  neutral: "text-muted-foreground",
};
